import type { JSX } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useUpdateSuperAdmin } from '../hooks/useUpdateSuperAdmin';
import type { SuperAdmin } from '../types/super-admin.types';
import type { EditSuperAdminFormData } from './EditSuperAdminDialog.utils';
import { useEditSuperAdminForm } from './EditSuperAdminDialog.hooks';
import { EditSuperAdminFormFields } from './EditSuperAdminFormFields';

interface EditSuperAdminDialogProps {
  superAdmin: SuperAdmin | null;
  onClose: () => void;
}

function FormButtons({
  isPending,
  onClose,
}: {
  isPending: boolean;
  onClose: () => void;
}): React.JSX.Element {
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onClose}>
        Cancel
      </Button>
      <Button type="submit" disabled={isPending}>
        {isPending ? 'Saving...' : 'Save'}
      </Button>
    </DialogFooter>
  );
}

export function EditSuperAdminDialog({
  superAdmin,
  onClose,
}: EditSuperAdminDialogProps): JSX.Element | null {
  const { updateSuperAdmin, isPending } = useUpdateSuperAdmin();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useEditSuperAdminForm(superAdmin);

  if (!superAdmin) return null;

  const onSubmit = (data: EditSuperAdminFormData): void => {
    updateSuperAdmin(
      {
        userId: superAdmin.id,
        data: { firstName: data.firstName, lastName: data.lastName, phone: data.phone ?? '' },
      },
      {
        onSuccess: () => {
          toast.success('Super admin updated successfully');
          onClose();
        },
        onError: () => {
          toast.error('Failed to update super admin');
        },
      }
    );
  };

  return (
    <Dialog open={superAdmin !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]" data-row-click-ignore>
        <DialogHeader>
          <DialogTitle>Edit Super Admin</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <EditSuperAdminFormFields register={register} errors={errors} />
          <FormButtons isPending={isPending} onClose={onClose} />
        </form>
      </DialogContent>
    </Dialog>
  );
}
